import '@/styles/dateRangeFilter.css'

import { ArrowLeftIcon, ArrowRightIcon, Button } from '@calizahq/react-components-v2'
import clsx from 'clsx'
import { addMonths, addWeeks, isEqual } from 'date-fns'
import React, { useEffect, useState } from 'react'
import DatePicker from 'react-datepicker'

import { Dropdown } from '@/components'
import ChipButton from '@/components/ListFilter/DateRangeFilter/ChipButton'
import { useAlertContext } from '@/context'
import { useMobile } from '@/hooks'
import { formatDate } from '@/util'

type Props = {
  className?: string
  disabled?: boolean
  disabledMessage?: string
  endDate?: Date
  headerTitle?: string
  listFilter?: boolean
  maxMoveInStartDate?: Date
  pastSelection?: boolean
  setEndDate: (d?: Date) => void
  setStartDate: (d?: Date) => void
  startDate?: Date
}

enum PresetRange {
  Day = '24',
  Month = '1 month',
  TwoWeeks = '2 weeks',
  Week = '7 days',
}

export const DateRangeSelection = ({
  className = '',
  setEndDate,
  setStartDate,
  startDate,
  endDate,
  disabled = false,
  listFilter = false,
  maxMoveInStartDate,
  pastSelection = false,
  headerTitle = 'Date Range',
  disabledMessage,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false)
  const { isMobile } = useMobile()
  const { setAlert } = useAlertContext()

  const [selectedStartDate, setSelectedStartDate] = useState(startDate)
  const [selectedEndDate, setSelectedEndDate] = useState(endDate)
  const [selectedPresetRange, setSelectedPresetRange] = useState<PresetRange | null>(null)
  const minDate = pastSelection ? undefined : maxMoveInStartDate ? maxMoveInStartDate : new Date()
  const maxDate = pastSelection ? new Date() : undefined

  useEffect(() => {
    setSelectedStartDate(startDate)
  }, [startDate])

  useEffect(() => {
    setSelectedEndDate(endDate)
  }, [endDate])

  useEffect(() => {
    if (selectedPresetRange) {
      const _startDate = selectedStartDate || new Date()
      let _endDate: null | Date = null

      switch (selectedPresetRange) {
        case PresetRange.Week:
          _endDate = addWeeks(_startDate, 1)
          updateDates(_startDate, _endDate)
          return
        case PresetRange.TwoWeeks:
          _endDate = addWeeks(_startDate, 2)
          updateDates(_startDate, _endDate)
          return
        case PresetRange.Month:
          _endDate = addMonths(_startDate, 1)
          updateDates(_startDate, _endDate)

          return
        default:
          return
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPresetRange])

  const isStartDateSelected = !!selectedStartDate
  const isEndDateSelected = !!selectedEndDate

  const updateDates = (startDate: Date, endDate: Date) => {
    if (startDate !== selectedStartDate) {
      setSelectedStartDate(startDate)
      setStartDate(startDate)
    }
    if (endDate !== selectedEndDate) {
      setSelectedEndDate(endDate)
      setEndDate(endDate)
    }
  }

  const onChange = (dates) => {
    const [start, end] = dates
    if (selectedPresetRange !== null) {
      setSelectedPresetRange(null)
    }
    if (isEqual(start, end)) {
      setSelectedEndDate(undefined)
      setEndDate(undefined)
    } else {
      setSelectedStartDate(start)
      setSelectedEndDate(end)
      setStartDate(start)
      setEndDate(end)
    }
  }

  const onCancel = () => {
    setIsOpen(false)
  }

  const onClear = () => {
    setSelectedPresetRange(null)
    setSelectedStartDate(undefined)
    setSelectedEndDate(undefined)
    setStartDate(undefined)
    setEndDate(undefined)
    setIsOpen(false)
  }

  const onPresetClick = (preset: PresetRange) => () => {
    setSelectedPresetRange(preset)
  }

  const onApply = () => {
    setIsOpen(false)
  }

  const renderFooter = () => {
    return (
      <div className="flex flex-row items-center justify-between px-6 pb-4 pt-4">
        {isMobile ? (
          <Button className="mr-4 flex-1 bg-grey-100" onClick={onClear} variant={'secondary'}>
            Reset
          </Button>
        ) : (
          <Button className="h-fit-content px-0" onClick={onClear} variant={'secondary'}>
            <span className="underline underline-offset-2">Reset</span>
          </Button>
        )}

        <Button
          className="flex-1 bg-text-semantic-success md:flex-0"
          onClick={onApply}
          size={isMobile ? undefined : 'small'}
        >
          Apply
        </Button>
      </div>
    )
  }

  const renderCalendarHeader = ({ date, decreaseMonth, increaseMonth }) => {
    return (
      <div className="align-items mb-2 flex justify-between px-4">
        <ArrowLeftIcon className="w-4 cursor-pointer" onClick={decreaseMonth} />
        <h3 style={{ fontWeight: 700 }}>{formatDate(date, 'LLLL yyyy')}</h3>
        <ArrowRightIcon className="w-4 cursor-pointer" onClick={increaseMonth} />
      </div>
    )
  }

  const displayStartDate = selectedStartDate ? formatDate(selectedStartDate, 'd MMM yyyy') : ''
  const displayEndDate = selectedEndDate ? formatDate(selectedEndDate, 'd MMM yyyy') : ''

  const dropdownHeader =
    displayStartDate || displayEndDate
      ? `${displayStartDate} ${displayEndDate ? `- ${displayEndDate}` : ''}`
      : headerTitle

  const handleToggleClick = () => {
    if (disabled && disabledMessage) {
      setAlert(disabledMessage, 'info')
    }
  }

  return (
    <Dropdown
      className={clsx('my-2 md:my-0', className)}
      disabled={disabled}
      expanded={isOpen}
      fullScreenMobile={false}
      headerTitle={dropdownHeader}
      onHideDropdown={onCancel}
      renderFooter={renderFooter}
      renderHeader={() => <></>}
      renderToggle={
        listFilter
          ? undefined
          : () => (
              <ToggleComponent
                disabled={disabled}
                dropdownHeader={dropdownHeader}
                handleToggleClick={handleToggleClick}
              />
            )
      }
      selected={!!(selectedStartDate || selectedEndDate)}
      setExpanded={setIsOpen}
    >
      <div className="flex w-full flex-col items-center md:w-95">
        <div className="mb-4 flex w-full items-center justify-between px-4 md:mt-4">
          <div className="mr-2 flex flex-1 flex-col rounded-lg bg-grey-100 px-4 py-2">
            <div className="grey-500 mb-1 text-xs">Starting from</div>
            <div className="">{isStartDateSelected ? formatDate(selectedStartDate, 'MMM d, yyyy') : 'Select Date'}</div>
          </div>
          <div
            className={clsx(
              'ml-2 flex flex-1 flex-col rounded-lg border border-grey-100 py-2 px-4',
              isEndDateSelected && 'bg-grey-100',
            )}
          >
            <div className="grey-500 mb-1 text-xs">Until</div>
            <div
              className={clsx(
                !isStartDateSelected && 'text-grey-500',
                isStartDateSelected && !isEndDateSelected && 'text-green-300',
              )}
            >
              {isEndDateSelected ? formatDate(selectedEndDate, 'MMM d, yyyy') : 'Optional'}
            </div>
          </div>
        </div>

        <DatePicker
          calendarClassName={isMobile ? 'date-range-filter-mobile' : 'date-range-filter'}
          disableKeyboardNavigation={true}
          disabled={disabled}
          disabledKeyboardNavigation={true}
          endDate={selectedEndDate || undefined}
          inline={true}
          maxDate={maxDate}
          minDate={minDate}
          onChange={onChange}
          renderCustomHeader={renderCalendarHeader}
          selected={selectedStartDate}
          selectsRange={true}
          startDate={selectedStartDate || undefined}
        />

        <div className="flex items-center justify-evenly border-t border-grey-200 px-4 py-4">
          <ChipButton
            className="mr-3"
            onClick={onPresetClick(PresetRange.Week)}
            selected={selectedPresetRange === PresetRange.Week}
          >
            7 days
          </ChipButton>
          <ChipButton
            className="mr-3"
            onClick={onPresetClick(PresetRange.TwoWeeks)}
            selected={selectedPresetRange === PresetRange.TwoWeeks}
          >
            2 weeks
          </ChipButton>
          <ChipButton onClick={onPresetClick(PresetRange.Month)} selected={selectedPresetRange === PresetRange.Month}>
            1 month
          </ChipButton>
        </div>
      </div>
    </Dropdown>
  )
}

type ToggleComponentProps = {
  disabled: boolean
  dropdownHeader: string
  handleToggleClick: () => void
}
function ToggleComponent({ handleToggleClick, dropdownHeader, disabled }: ToggleComponentProps) {
  return (
    <div className="underline" onClick={handleToggleClick} role="presentation">
      {disabled ? 'This is disabled' : dropdownHeader}
    </div>
  )
}
